import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Upload, Search, Loader2, FolderOpen, Folder, FileText, Image, File,
  Trash2, Download, MoreHorizontal, Grid3X3, List, ExternalLink,
  FolderPlus, ChevronRight, Home, FolderInput, Pencil, Link2, Link as LinkIcon,
  Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  return File;
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function FilesPage() {
  const { currentCompany } = useCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const folderFromUrl = searchParams.get('folder');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id,name,parent_id}, ...] root -> current
  const [currentFolderId, setCurrentFolderId] = useState(folderFromUrl || null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [deleteLinkTarget, setDeleteLinkTarget] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [renameFileTarget, setRenameFileTarget] = useState(null);
  const [renameFileName, setRenameFileName] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState({ title: '', url: '', notes: '' });
  const [moveTarget, setMoveTarget] = useState(null); // {kind:'file'|'folder'|'link', item}
  const [moveTreeFolders, setMoveTreeFolders] = useState([]);
  const [moveTreeParent, setMoveTreeParent] = useState(null);
  const [moveTreeBreadcrumb, setMoveTreeBreadcrumb] = useState([]);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      if (search) {
        const params = { company_id: currentCompany.id, search, in_folder: 'all' };
        const [filesRes, linksRes] = await Promise.all([
          axios.get(`${API_URL}/api/files`, { params, withCredentials: true }),
          axios.get(`${API_URL}/api/links`, { params, withCredentials: true }),
        ]);
        setFiles(filesRes.data);
        setLinks(linksRes.data);
        setFolders([]);
      } else {
        const folderParams = { company_id: currentCompany.id };
        if (currentFolderId) folderParams.parent_id = currentFolderId;
        const itemParams = { company_id: currentCompany.id };
        if (currentFolderId) itemParams.folder_id = currentFolderId;
        const [foldersRes, filesRes, linksRes, breadcrumbRes] = await Promise.all([
          axios.get(`${API_URL}/api/folders`, { params: folderParams, withCredentials: true }),
          axios.get(`${API_URL}/api/files`, { params: itemParams, withCredentials: true }),
          axios.get(`${API_URL}/api/links`, { params: itemParams, withCredentials: true }),
          currentFolderId
            ? axios.get(`${API_URL}/api/folders/breadcrumb`, {
                params: { company_id: currentCompany.id, folder_id: currentFolderId },
                withCredentials: true,
              })
            : Promise.resolve({ data: [] }),
        ]);
        setFolders(foldersRes.data);
        setFiles(filesRes.data);
        setLinks(linksRes.data);
        setBreadcrumb(breadcrumbRes.data);
      }
    } catch {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [currentCompany, currentFolderId, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset folder when company changes
  useEffect(() => {
    setCurrentFolderId(null);
    setBreadcrumb([]);
    setSearch('');
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  // Sync URL → state (handles back/forward + direct paste of shared link)
  useEffect(() => {
    const fromUrl = searchParams.get('folder') || null;
    if (fromUrl !== currentFolderId) {
      setCurrentFolderId(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sync state → URL (so the URL is always shareable)
  useEffect(() => {
    const current = searchParams.get('folder') || null;
    if (current !== currentFolderId) {
      const next = new URLSearchParams(searchParams);
      if (currentFolderId) {
        next.set('folder', currentFolderId);
      } else {
        next.delete('folder');
      }
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  const copyFolderLink = async (folder) => {
    const base = `${window.location.origin}${location.pathname}`;
    const url = `${base}?folder=${folder.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link to "${folder.name}" copied`);
    } catch {
      // Fallback for older browsers / iframes
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success(`Link to "${folder.name}" copied`);
      } catch {
        toast.error('Could not copy link');
      }
      document.body.removeChild(ta);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({ company_id: currentCompany.id });
      if (currentFolderId) params.append('folder_id', currentFolderId);
      await axios.post(`${API_URL}/api/files/upload?${params.toString()}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      toast.error('Folder name is required');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/folders`,
        { company_id: currentCompany.id, name, parent_id: currentFolderId },
        { withCredentials: true }
      );
      toast.success(`Folder "${name}" created`);
      setNewFolderName('');
      setCreateFolderOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleRenameFolder = async () => {
    const name = renameFolderName.trim();
    if (!name || !renameFolderTarget) return;
    try {
      await axios.put(
        `${API_URL}/api/folders/${renameFolderTarget.id}`,
        { name },
        { withCredentials: true }
      );
      toast.success('Folder renamed');
      setRenameFolderTarget(null);
      setRenameFolderName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename folder');
    }
  };

  const handleRenameFile = async () => {
    const name = renameFileName.trim();
    if (!name || !renameFileTarget) return;
    try {
      await axios.put(`${API_URL}/api/files/${renameFileTarget.id}/rename`, { filename: name });
      toast.success('File renamed');
      setRenameFileTarget(null);
      setRenameFileName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename file');
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      await axios.delete(`${API_URL}/api/files/${deleteFileTarget.id}`, { withCredentials: true });
      toast.success('File deleted');
      setDeleteFileTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to delete file');
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      await axios.delete(`${API_URL}/api/folders/${deleteFolderTarget.id}`, { withCredentials: true });
      toast.success(`"${deleteFolderTarget.name}" and contents deleted`);
      setDeleteFolderTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleDownload = (file) => {
    window.open(`${API_URL}/api/files/${file.id}/download`, '_blank');
  };

  // ---- Link handlers ----
  const openCreateLink = () => {
    setEditingLink(null);
    setLinkForm({ title: '', url: '', notes: '' });
    setLinkDialogOpen(true);
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setLinkForm({ title: link.title, url: link.url, notes: link.notes || '' });
    setLinkDialogOpen(true);
  };

  const handleSaveLink = async () => {
    const url = linkForm.url.trim();
    if (!url) {
      toast.error('URL is required');
      return;
    }
    const payload = {
      url,
      title: linkForm.title.trim() || url,
      notes: linkForm.notes.trim(),
    };
    try {
      if (editingLink) {
        await axios.put(`${API_URL}/api/links/${editingLink.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Link updated');
      } else {
        await axios.post(
          `${API_URL}/api/links`,
          { ...payload, company_id: currentCompany.id, folder_id: currentFolderId },
          { withCredentials: true }
        );
        toast.success('Link saved');
      }
      setLinkDialogOpen(false);
      setEditingLink(null);
      setLinkForm({ title: '', url: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save link');
    }
  };

  const handleDeleteLink = async () => {
    if (!deleteLinkTarget) return;
    try {
      await axios.delete(`${API_URL}/api/links/${deleteLinkTarget.id}`, { withCredentials: true });
      toast.success('Link deleted');
      setDeleteLinkTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to delete link');
    }
  };

  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  const enterFolder = (folder) => {
    setSearch('');
    setCurrentFolderId(folder.id);
  };

  const navigateBreadcrumb = (folderId) => {
    setSearch('');
    setCurrentFolderId(folderId);
  };

  // ---- Move dialog ----
  const openMoveDialog = async (kind, item) => {
    setMoveTarget({ kind, item });
    setMoveTreeParent(null);
    setMoveTreeBreadcrumb([]);
    try {
      const res = await axios.get(`${API_URL}/api/folders`, {
        params: { company_id: currentCompany.id },
        withCredentials: true,
      });
      setMoveTreeFolders(res.data);
    } catch {
      toast.error('Failed to load folders');
    }
  };

  const navigateMoveTree = async (folderId) => {
    setMoveTreeParent(folderId);
    try {
      const params = { company_id: currentCompany.id };
      if (folderId) params.parent_id = folderId;
      const [foldersRes, breadcrumbRes] = await Promise.all([
        axios.get(`${API_URL}/api/folders`, { params, withCredentials: true }),
        folderId
          ? axios.get(`${API_URL}/api/folders/breadcrumb`, {
              params: { company_id: currentCompany.id, folder_id: folderId },
              withCredentials: true,
            })
          : Promise.resolve({ data: [] }),
      ]);
      setMoveTreeFolders(foldersRes.data);
      setMoveTreeBreadcrumb(breadcrumbRes.data);
    } catch {
      toast.error('Failed to navigate folders');
    }
  };

  const handleMove = async (targetFolderId) => {
    if (!moveTarget) return;
    try {
      if (moveTarget.kind === 'file') {
        await axios.put(
          `${API_URL}/api/files/${moveTarget.item.id}/move`,
          { folder_id: targetFolderId },
          { withCredentials: true }
        );
      } else if (moveTarget.kind === 'link') {
        await axios.put(
          `${API_URL}/api/links/${moveTarget.item.id}`,
          { folder_id: targetFolderId },
          { withCredentials: true }
        );
      } else {
        await axios.put(
          `${API_URL}/api/folders/${moveTarget.item.id}`,
          { parent_id: targetFolderId },
          { withCredentials: true }
        );
      }
      toast.success('Moved');
      setMoveTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move');
    }
  };

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a company</p>
      </div>
    );
  }

  const isEmpty = !loading && folders.length === 0 && files.length === 0 && links.length === 0;

  return (
    <div className="space-y-6" data-testid="files-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Files</h1>
          <p className="text-gray-500 mt-1">Organize documents in folders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setNewFolderName('');
              setCreateFolderOpen(true);
            }}
            data-testid="new-folder-btn"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
          <Button variant="outline" onClick={openCreateLink} data-testid="add-link-btn">
            <Link2 className="w-4 h-4 mr-2" />
            Add Link
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            data-testid="file-upload-input"
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </div>
      </div>

      {/* SharePoint shortcut for Veriquant */}
      {currentCompany?.slug === 'veriquant' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="https://veriquant.sharepoint.com/:f:/s/VeriQuant9-OneDrivee/IgB9HLpkBnVeQ73NGQ4A1KMGAT_BRXa29fn1uEYk_at_uFs?e=FmUfBL"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors group"
            data-testid="sharepoint-link"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <FolderOpen className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-900">VeriQuant SharePoint</p>
              <p className="text-xs text-blue-600 truncate">veriquant.sharepoint.com</p>
            </div>
            <ExternalLink className="w-4 h-4 text-blue-400 group-hover:text-blue-600 flex-shrink-0" />
          </a>
        </div>
      )}

      {/* Search + view toggle */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search all files in this company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-files"
          />
        </div>
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            data-testid="view-list"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            data-testid="view-grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      {!search && (
        <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="breadcrumb">
          <button
            onClick={() => navigateBreadcrumb(null)}
            className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 ${
              !currentFolderId ? 'text-gray-900 font-medium' : 'text-gray-600'
            }`}
            data-testid="breadcrumb-root"
          >
            <Home className="w-4 h-4" />
            {currentCompany.name}
          </button>
          {breadcrumb.map((b, idx) => (
            <div key={b.id} className="flex items-center gap-1">
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => navigateBreadcrumb(b.id)}
                className={`px-2 py-1 rounded hover:bg-gray-100 ${
                  idx === breadcrumb.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-600'
                }`}
                data-testid={`breadcrumb-${b.id}`}
              >
                {b.name}
              </button>
            </div>
          ))}
          {currentFolderId && breadcrumb.length > 0 && (
            <button
              onClick={() => copyFolderLink(breadcrumb[breadcrumb.length - 1])}
              className="ml-2 flex items-center gap-1.5 px-2 py-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900 text-xs font-medium"
              title="Copy link to this folder"
              data-testid="copy-current-folder-link"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy link
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : isEmpty ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {search ? 'No files match your search' : 'This folder is empty'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Try a different keyword' : 'Upload a file or create a folder to get started'}
          </p>
          {!search && (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setCreateFolderOpen(true)}>
                <FolderPlus className="w-4 h-4 mr-2" />
                New Folder
              </Button>
              <Button variant="outline" onClick={openCreateLink}>
                <Link2 className="w-4 h-4 mr-2" />
                Add Link
              </Button>
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Upload File
              </Button>
            </div>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {folders.map((folder) => (
                <tr
                  key={`folder-${folder.id}`}
                  className="hover:bg-blue-50 cursor-pointer"
                  onDoubleClick={() => enterFolder(folder)}
                  data-testid={`folder-row-${folder.id}`}
                >
                  <td className="px-4 py-3" onClick={() => enterFolder(folder)}>
                    <div className="flex items-center gap-3">
                      <Folder className="w-5 h-5 text-blue-500 fill-blue-100" />
                      <span className="font-medium text-gray-900 truncate max-w-xs">{folder.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-sm">Folder</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{folder.file_count} item{folder.file_count === 1 ? '' : 's'}</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{new Date(folder.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`folder-menu-${folder.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => enterFolder(folder)}>
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyFolderLink(folder)} data-testid={`copy-folder-link-${folder.id}`}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setRenameFolderTarget(folder);
                            setRenameFolderName(folder.name);
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMoveDialog('folder', folder)}>
                          <FolderInput className="w-4 h-4 mr-2" />
                          Move
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteFolderTarget(folder)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {files.map((file) => {
                const Icon = getFileIcon(file.content_type);
                return (
                  <tr key={`file-${file.id}`} className="hover:bg-gray-50" data-testid={`file-row-${file.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900 truncate max-w-xs">{file.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {file.content_type?.split('/')[1]?.toUpperCase() || 'FILE'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{formatFileSize(file.size)}</td>
                    <td className="px-4 py-3 text-gray-500 text-sm">
                      {new Date(file.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDownload(file)}>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setRenameFileTarget(file); setRenameFileName(file.filename); }} data-testid={`rename-file-${file.id}`}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openMoveDialog('file', file)}>
                            <FolderInput className="w-4 h-4 mr-2" />
                            Move to...
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteFileTarget(file)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {links.map((link) => (
                <tr key={`link-${link.id}`} className="hover:bg-gray-50" data-testid={`link-row-${link.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <LinkIcon className="w-5 h-5 text-blue-500" />
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 truncate max-w-xs hover:text-blue-700 hover:underline"
                        title={link.url}
                      >
                        {link.title}
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-sm">Link</td>
                  <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-xs">{getDomain(link.url)}</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{new Date(link.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(link.url, '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open link
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditLink(link)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMoveDialog('link', link)}>
                          <FolderInput className="w-4 h-4 mr-2" />
                          Move to...
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteLinkTarget(link)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-6">
          {folders.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Folders</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                    onDoubleClick={() => enterFolder(folder)}
                    data-testid={`folder-card-${folder.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"
                        onClick={() => enterFolder(folder)}
                      >
                        <Folder className="w-5 h-5 text-blue-600" />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => enterFolder(folder)}>
                            <FolderOpen className="w-4 h-4 mr-2" />
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => copyFolderLink(folder)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy link
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setRenameFolderTarget(folder);
                              setRenameFolderName(folder.name);
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openMoveDialog('folder', folder)}>
                            <FolderInput className="w-4 h-4 mr-2" />
                            Move
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteFolderTarget(folder)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate" onClick={() => enterFolder(folder)}>
                      {folder.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {folder.file_count} item{folder.file_count === 1 ? '' : 's'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {files.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Files</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {files.map((file) => {
                  const Icon = getFileIcon(file.content_type);
                  return (
                    <div
                      key={file.id}
                      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                      data-testid={`file-card-${file.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-gray-500" />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDownload(file)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setRenameFileTarget(file); setRenameFileName(file.filename); }} data-testid={`rename-file-card-${file.id}`}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openMoveDialog('file', file)}>
                              <FolderInput className="w-4 h-4 mr-2" />
                              Move to...
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteFileTarget(file)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatFileSize(file.size)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {links.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Links</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all"
                    data-testid={`link-card-${link.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center hover:bg-blue-100"
                      >
                        <LinkIcon className="w-5 h-5 text-blue-600" />
                      </a>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => window.open(link.url, '_blank')}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open link
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditLink(link)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openMoveDialog('link', link)}>
                            <FolderInput className="w-4 h-4 mr-2" />
                            Move to...
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteLinkTarget(link)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-900 truncate block hover:text-blue-700 hover:underline"
                      title={link.url}
                    >
                      {link.title}
                    </a>
                    <p className="text-xs text-gray-500 mt-1 truncate">{getDomain(link.url)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Folder</DialogTitle>
            <DialogDescription>
              {breadcrumb.length > 0
                ? `Create a folder inside "${breadcrumb[breadcrumb.length - 1].name}"`
                : `Create a folder in ${currentCompany.name}`}
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
            }}
            data-testid="new-folder-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} data-testid="create-folder-confirm">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={!!renameFolderTarget} onOpenChange={(open) => !open && setRenameFolderTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameFolder();
            }}
            data-testid="rename-folder-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleRenameFolder}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename File Dialog */}
      <Dialog open={!!renameFileTarget} onOpenChange={(open) => !open && setRenameFileTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename File</DialogTitle>
            <DialogDescription>The original file extension is preserved automatically.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={renameFileName}
            onChange={(e) => setRenameFileName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFile(); }}
            data-testid="rename-file-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFileTarget(null)}>Cancel</Button>
            <Button onClick={handleRenameFile} data-testid="rename-file-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(open) => !open && setMoveTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Move {moveTarget?.kind === 'folder' ? 'folder' : 'file'} "{moveTarget?.item?.name || moveTarget?.item?.filename}"
            </DialogTitle>
            <DialogDescription>Choose a destination folder</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-1 text-sm flex-wrap mb-2">
            <button
              onClick={() => navigateMoveTree(null)}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-700"
            >
              <Home className="w-3.5 h-3.5" />
              {currentCompany.name}
            </button>
            {moveTreeBreadcrumb.map((b) => (
              <div key={b.id} className="flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                <button
                  onClick={() => navigateMoveTree(b.id)}
                  className="px-2 py-1 rounded hover:bg-gray-100 text-gray-700"
                >
                  {b.name}
                </button>
              </div>
            ))}
          </div>

          <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
            {moveTreeFolders.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No subfolders here</p>
            ) : (
              moveTreeFolders.map((f) => {
                const isSelf = moveTarget?.kind === 'folder' && f.id === moveTarget.item.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => navigateMoveTree(f.id)}
                    disabled={isSelf}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-left ${
                      isSelf ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                  >
                    <Folder className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-900">{f.name}</span>
                    {isSelf && <span className="text-xs text-gray-400 ml-auto">(this folder)</span>}
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter className="flex sm:justify-between">
            <Button variant="outline" onClick={() => setMoveTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => handleMove(moveTreeParent)} data-testid="move-confirm-btn">
              Move here
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Confirmation */}
      <AlertDialog open={!!deleteFileTarget} onOpenChange={(open) => !open && setDeleteFileTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteFileTarget?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Folder Confirmation */}
      <AlertDialog open={!!deleteFolderTarget} onOpenChange={(open) => !open && setDeleteFolderTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteFolderTarget?.name}" and all of its contents (subfolders and files) will be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFolder} className="bg-red-600 hover:bg-red-700">
              Delete folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Create/Edit Dialog */}
      <Dialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          if (!open) {
            setEditingLink(null);
            setLinkForm({ title: '', url: '', notes: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLink ? 'Edit Link' : 'Add Link'}</DialogTitle>
            <DialogDescription>
              {editingLink
                ? 'Update the URL or label.'
                : breadcrumb.length > 0
                ? `Save a link inside "${breadcrumb[breadcrumb.length - 1].name}"`
                : `Save a link in ${currentCompany.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">URL *</label>
              <Input
                autoFocus
                placeholder="https://example.com/document"
                value={linkForm.url}
                onChange={(e) => setLinkForm((p) => ({ ...p, url: e.target.value }))}
                onPaste={(e) => {
                  const pasted = e.clipboardData?.getData('text');
                  if (pasted && !linkForm.title) {
                    try {
                      const u = new URL(pasted.startsWith('http') ? pasted : `https://${pasted}`);
                      setLinkForm((p) => ({ ...p, title: u.hostname.replace(/^www\./, '') }));
                    } catch {/* ignore */}
                  }
                }}
                data-testid="link-url-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Label</label>
              <Input
                placeholder="Optional — defaults to URL"
                value={linkForm.title}
                onChange={(e) => setLinkForm((p) => ({ ...p, title: e.target.value }))}
                data-testid="link-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notes</label>
              <Input
                placeholder="Optional notes"
                value={linkForm.notes}
                onChange={(e) => setLinkForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLink} data-testid="save-link-btn">
              {editingLink ? 'Save changes' : 'Add link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Link Confirmation */}
      <AlertDialog open={!!deleteLinkTarget} onOpenChange={(open) => !open && setDeleteLinkTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteLinkTarget?.title}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLink} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
